import React, { Component, cloneElement } from 'react';

import {arrayMove} from 'react-sortable-hoc';

import styled from 'styled-components';

import ManagedComponent from '../components/managed-component';
import OriginalSortableList from '../components/List/sortable-list';
import OriginalList from '../components/List/list';
import TextInput from './text-input';
import Text from './text';
import BasicButton from '../components/basic-button';
import { SectionChild } from '../components/section';
import theme, { isiOS, isThemeiOS } from '../themes';

const ToggleEditButton = styled(BasicButton)`
  ${theme.ToggleEditButton }
`;

const baseListStyle = `
  ${!isiOS() ? `
    margin-top: -16px;
  ` : ''}
`;

const SortableList = styled(OriginalSortableList)`
  ${baseListStyle}
`;

const List = styled(OriginalList)`
  ${baseListStyle}
`;

class AdditiveList extends Component {
  static _type = 'AdditiveList';
  _type = 'AdditiveList';

  static defaultProps = {
    selectedItems: []
  };

  state = {
    canEdit: false
  };

  get selectedItems() {
    const { selectedItems } = this.props;

    if (Array.isArray(selectedItems)) {
      return selectedItems;
    }

    return [];
  }

  canEdit = (selectedItems) =>
    selectedItems.length > 0 ? this.state.canEdit : false;

  onListChange = (selectedItems) => {
    const { onListChange } = this.props;

    this.setState(() => {
      if (typeof onListChange === 'function') {
        onListChange(selectedItems);
      }

      return {
        canEdit: this.canEdit(selectedItems)
      };
    });
  };

  addItem = (...items) => {
    const selectedItems = this.selectedItems;
    const existingNames = selectedItems.map(({ name }) => name);
    const newItems = items.filter(({ name }) => !existingNames.includes(name));
    if (newItems.length > 0) {
      this.onListChange([...selectedItems, ...newItems]);
    }
  };

  toggleEdit = () => {
    this.setState({
      canEdit: !this.state.canEdit
    });
  };

  deleteItem = (index: number) => {
    this.onListChange(this.selectedItems.filter((_,i) => i !== index));
  };

  onSortEnd = ({ oldIndex, newIndex }) => {
    this.onListChange(arrayMove(this.selectedItems, oldIndex, newIndex));
  };

  defaultRender = ({ name }) => <Text>{name}</Text>;

  defaultAddAction = (() => {
    let label;
    if (isThemeiOS()) {
      label='Add List Item ...';
    } else {
      label = '+ Add List Item'
    }
    return <TextInput label={label} />
  })();

  render() {
    const {
      title,
      description,
      maxItems,
      addAction = this.defaultAddAction,
      renderItem = this.defaultRender
    } = this.props;

    const { canEdit } = this.state;
    const selectedItems = this.selectedItems;

    let list;
    if (!canEdit && selectedItems) {
      let addActionProps = {
        disabled: selectedItems.length >= maxItems,
        renderItem,
        actAsButton: true,
        resetOnClose: true,
        // TextInput value saved
        onChange: (value) => this.addItem(value)
      };

      if (addAction.props.options) {
        const selectedNames = selectedItems.map(({ name }) => name);
        const optionsMap = addAction.props.options.reduce((optionsMap, { name }, index) => {
          optionsMap[name] = index;
          return optionsMap;
        }, {});
        addActionProps = {
          ...addActionProps,
          multiple: true,
          selected: selectedNames.map((name) => optionsMap[name]),
          onSelection: ({ values }) => values.map((value) => this.addItem(value))
        };
      }

      list = (
        <List
          addEl={cloneElement(addAction, addActionProps)}
          items={selectedItems}
          renderItem={renderItem}
          onDelete={(index) => this.deleteItem(index)}
        />
      );
    } else if (selectedItems) {
      list = (
        <SortableList
          items={selectedItems}
          onSortEnd={this.onSortEnd}
          useDragHandle
          lockAxis="y"
          lockOffset="0px"
          lockToContainerEdges
          helperClass="sortable-helper"
          canEdit={canEdit}
          renderItem={renderItem}
          onDelete={(index) => this.deleteItem(index)}
        />
      );
    }

    const hasSelectedItems = selectedItems && selectedItems.length > 0;

    const iOSToggle = hasSelectedItems && isThemeiOS() && (
      <ToggleEditButton onClick={this.toggleEdit}>
        {canEdit ? 'Done' : 'Edit'}
      </ToggleEditButton>
    );

    return (
      <SectionChild
        title={title}
        description={description}
        collapse
      >
        {hasSelectedItems && !isThemeiOS() &&
          <ToggleEditButton onClick={this.toggleEdit}>
            {canEdit ? 'Done' : 'Edit'}
          </ToggleEditButton>
        }
        {list}
        {iOSToggle}
      </SectionChild>
    );
  }
}

export default class extends Component {
  parseValue(rawValue) {
    try {
      return JSON.parse(rawValue);
    } catch (err) {
      return;
    }
  }

  render() {
    const {
      title,
      description,
      maxItems,
      selectedItems,
      addAction,
      renderItem,
      onListChange,
      settingsKey
    } = this.props;

    const listProps = {
      title,
      description,
      maxItems,
      selectedItems,
      addAction,
      renderItem,
      onListChange
    };

    if (!settingsKey) {
      return (
        <AdditiveList {...listProps} />
      );
    }

    return (
      <ManagedComponent settingsKey={settingsKey}>
        {({ value, onChange }) => (
          <AdditiveList
            {...listProps}
            selectedItems={this.parseValue(value)}
            onListChange={(selectedItems) => {
              if (typeof onListChange === 'function') {
                onListChange(selectedItems);
              }
              onChange(JSON.stringify(selectedItems));
            }}
          />
        )}
      </ManagedComponent>
    );
  }
}



// WEBPACK FOOTER //
// ./src/fitbit-components/additive-list.js